import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { getEarnedAchievements, ACHIEVEMENTS, getAgeBand } from '../utils/ageBands';
import { logout } from '../api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogOut, FiEdit2, FiStar, FiAward, FiTrendingUp, FiTarget } = FiIcons;

function StatCard({ emoji, value, label, color }) {
  return (
    <div className={`${color} rounded-2xl p-4 text-center border-2`}>
      <div className="text-2xl mb-1">{emoji}</div>
      <div className="font-kid text-jungle-700 text-xl">{value}</div>
      <div className="font-comic text-jungle-600 text-xs">{label}</div>
    </div>
  );
}

function AchievementBadge({ achievement, earned }) {
  return (
    <motion.div whileHover={{ scale: 1.05 }}
      className={`rounded-2xl p-4 text-center border-2 transition-all ${earned
        ? 'bg-yellow-50 border-yellow-300 shadow-md'
        : 'bg-gray-50 border-gray-200 opacity-40 grayscale'}`}>
      <div className="text-3xl mb-2">{achievement.emoji}</div>
      <div className="font-kid text-sm text-jungle-700 leading-tight">{achievement.label}</div>
      <div className="font-comic text-xs text-gray-500 mt-1">{achievement.description}</div>
      {earned && (
        <div className="mt-2 bg-yellow-400 text-white text-xs font-kid px-2 py-0.5 rounded-full inline-block">
          Earned! ✓
        </div>
      )}
    </motion.div>
  );
}

function Profile() {
  const { state, dispatch } = useGame();
  const navigate = useNavigate();
  const earnedAchievements = getEarnedAchievements(state);
  const band = state.ageBand || getAgeBand(state.age || 7);
  const accuracy = state.gameStats.totalAnswers > 0
    ? Math.round((state.gameStats.correctAnswers / state.gameStats.totalAnswers) * 100)
    : 0;

  const avatarEmojis = { lion: '🦁', tiger: '🐯', elephant: '🐘', monkey: '🐵', panda: '🐼', dragon: '🐉' };

  const handleLogout = () => {
    logout();
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-4 pt-24 pb-32">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Hero Card */}
        <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 text-center">
          <div className="relative inline-block mb-4">
            <div className={`w-28 h-28 rounded-full flex items-center justify-center text-5xl border-4 border-safari-300 bg-gradient-to-br ${band.color} shadow-xl`}>
              {avatarEmojis[state.avatar?.character] || '🦁'}
            </div>
            <div className={`absolute -bottom-2 -right-2 ${band.bgColor} ${band.borderColor} border-2 rounded-full px-2 py-0.5 text-xs font-kid ${band.textColor}`}>
              {band.emoji} {band.label}
            </div>
          </div>
          <h1 className="text-3xl font-kid text-jungle-700 mb-1">{state.playerName || 'Explorer'}</h1>
          <p className="font-comic text-jungle-500 text-sm">Age {state.age} · Level {state.level} Explorer</p>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center mt-6">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/avatar')}
              className="flex items-center gap-2 bg-jungle-500 text-white px-5 py-2 rounded-2xl font-kid shadow-lg hover:bg-jungle-600 transition-all">
              <SafeIcon icon={FiEdit2} /> Customize Avatar
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-100 text-red-600 border-2 border-red-200 px-5 py-2 rounded-2xl font-kid hover:bg-red-200 transition-all">
              <SafeIcon icon={FiLogOut} /> Logout
            </motion.button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard emoji="🪙" value={state.tokens} label="Tokens" color="bg-yellow-50 border-yellow-200" />
          <StatCard emoji="🏆" value={state.completedQuests?.length || 0} label="Quests Done" color="bg-green-50 border-green-200" />
          <StatCard emoji="🎯" value={`${accuracy}%`} label="Accuracy" color="bg-blue-50 border-blue-200" />
          <StatCard emoji="⭐" value={`Lvl ${state.level}`} label="Level" color="bg-purple-50 border-purple-200" />
        </motion.div>

        {/* Progress Bar */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl p-6 shadow-xl border-4 border-safari-200">
          <div className="flex items-center gap-2 mb-3">
            <SafeIcon icon={FiTrendingUp} className="text-jungle-500" />
            <h2 className="font-kid text-jungle-700 text-lg">Level Progress</h2>
          </div>
          <div className="flex justify-between text-xs font-comic text-jungle-600 mb-1">
            <span>Level {state.level}</span>
            <span>{state.score % 200} / 200 XP</span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden border-2 border-gray-200">
            <motion.div initial={{ width: 0 }} animate={{ width: `${(state.score % 200) / 2}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className={`h-full bg-gradient-to-r ${band.color} rounded-full`} />
          </div>
          <p className="font-comic text-xs text-jungle-500 mt-2 text-center">
            {200 - (state.score % 200)} XP to Level {state.level + 1}
          </p>
        </motion.div>

        {/* Achievements */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl p-6 shadow-xl border-4 border-safari-200">
          <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={FiAward} className="text-yellow-500 text-xl" />
            <h2 className="font-kid text-jungle-700 text-xl">Achievements</h2>
            <span className="ml-auto bg-yellow-100 text-yellow-700 font-kid text-sm px-3 py-1 rounded-full">
              {earnedAchievements.length}/{ACHIEVEMENTS.length}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {ACHIEVEMENTS.map(achievement => (
              <AchievementBadge key={achievement.id} achievement={achievement}
                earned={earnedAchievements.some(e => e.id === achievement.id)} />
            ))}
          </div>
        </motion.div>

        {/* Age Band Info */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
          className={`rounded-3xl p-6 shadow-xl border-4 ${band.bgColor} ${band.borderColor}`}>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{band.emoji}</span>
            <div>
              <h3 className={`font-kid text-xl ${band.textColor}`}>{band.label} Explorer</h3>
              <p className="font-comic text-sm text-gray-600">Ages {band.range[0]}–{band.range[1]} · {band.description}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Profile;